"use client";

import * as api from "@/src/api/sdk.gen";
import { Search, Building2, ArrowRight, LayoutDashboard, Clock, BookOpen, Target, ShieldCheck, Calendar } from "lucide-react";
import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { useCourseNavigation } from "@/lib/contexts/CourseNavigationContext";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/src/lib/next-compat";
import { useUserProfile } from "@/src/lib/hooks/useUserProfile";
import CourseCard from "@/components/student/CourseCard";
import type { CourseCard as CourseCardData } from "@/lib/types/course";
import { CoursesGridSkeleton } from "./CoursesListSkeleton";

export default function CoursesList({
    page,
    limit,
    cacheKey,
}: {
    page: number;
    limit: number;
    cacheKey: string;
}) {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedOrgSlug, setSelectedOrgSlug] = useState<string | null>(null);
    const { data: userProfile, isLoading: isProfileLoading } = useUserProfile();

    const approvedOrgs = (userProfile?.organizations || []).filter(
        (org) => !org.status || org.status === "approved"
    );
    const adminOrgs = approvedOrgs.filter(
        (org) => !org.role || org.role.toLowerCase() === "admin" || org.role.toLowerCase() === "manager" || org.role.toLowerCase() === "superadmin"
    );
    const pendingOrgs = (userProfile?.organizations || []).filter(
        (org) => org.status === "pending"
    );
    const isPendingOnlyMember = pendingOrgs.length > 0 && approvedOrgs.length === 0;

    const memberPortalQuery = useQuery({
        queryKey: ["b2b-member-portal-overview", approvedOrgs.map((o) => o.id).join(",")],
        queryFn: async () => {
            const { data } = await api.getMemberPortalOverviewApiV1B2bMemberPortalOverviewGet();
            return data;
        },
        enabled: approvedOrgs.length > 0,
        staleTime: 30_000,
    });

    const portalOrgs = memberPortalQuery.data?.organizations || [];
    const activePortalOrg =
        portalOrgs.find((o) => o.organization_slug === selectedOrgSlug) ||
        portalOrgs[0] ||
        null;

    const currentOrg =
        activePortalOrg ||
        (approvedOrgs.length > 0
            ? {
                organization_id: approvedOrgs[0].id,
                organization_name: approvedOrgs[0].name,
                organization_slug: approvedOrgs[0].slug,
                role: approvedOrgs[0].role || "member",
                department: undefined,
                logo_url: approvedOrgs[0].logo_url,
                assigned_courses: [] as any[],
                skill_expectations: [] as any[],
                learning_rules: [] as any[],
            }
            : null);

    const isCurrentOrgAdmin = currentOrg
        ? adminOrgs.some((o) => o.slug === currentOrg.organization_slug || o.id === currentOrg.organization_id)
        : false;

    const orgLogoUrl = currentOrg
        ? (approvedOrgs.find((o) => o.slug === currentOrg.organization_slug || o.id === currentOrg.organization_id)?.logo_url)
        : null;

    if (isProfileLoading && !userProfile) {
        return <CoursesGridSkeleton />;
    }

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-5">
            {/* Pending Membership Notice */}
            {pendingOrgs.length > 0 && (
                <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-amber-900">
                    <div className="flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 shrink-0">
                            <Clock className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm sm:text-base font-bold text-amber-950">
                                    Membership Pending Approval • {pendingOrgs.map((o) => o.name).join(", ")}
                                </h3>
                                <span className="px-2 py-0.5 bg-amber-200/80 text-amber-900 text-[10px] font-bold uppercase rounded-md">
                                    Pending
                                </span>
                            </div>
                            <p className="text-xs text-amber-800 mt-0.5">
                                Your membership request is currently pending organization administrator review. Sponsored courses and workspace materials will unlock once approved.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Unified Organization Workspace Header */}
            {currentOrg && (
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white rounded-2xl p-4 sm:p-5 shadow-lg border border-indigo-500/30 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3.5">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-600/30 border border-indigo-400/40 flex items-center justify-center text-indigo-300 shadow-inner flex-shrink-0">
                                {orgLogoUrl ? (
                                    <img src={orgLogoUrl} alt={currentOrg.organization_name} className="w-8 h-8 object-contain rounded-lg" />
                                ) : (
                                    <Building2 className="w-6 h-6" />
                                )}
                            </div>
                            <div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="text-base sm:text-lg font-bold text-white tracking-tight">
                                        {currentOrg.organization_name}
                                    </h2>
                                    <span className="text-xs text-indigo-200/80 font-medium">
                                        Workspace
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                        isCurrentOrgAdmin
                                            ? "bg-indigo-500/20 border border-indigo-400/30 text-indigo-300"
                                            : "bg-slate-700/60 border border-slate-600 text-slate-300"
                                    }`}>
                                        {isCurrentOrgAdmin ? "Org Admin" : currentOrg.role || "Member"}
                                    </span>
                                    {currentOrg.department && (
                                        <span className="px-2 py-0.5 rounded-md bg-white/10 border border-white/20 text-slate-200 text-[10px] font-semibold">
                                            {currentOrg.department}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-slate-300 mt-1">
                                    {isCurrentOrgAdmin
                                        ? "Manage employee enrollments, cohort assignments, knowledge base, and team skill intelligence."
                                        : "Assigned training requirements, deadlines, and skill expectations for your workspace."}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5 flex-shrink-0 flex-wrap">
                            {/* Organization switcher if member of multiple orgs */}
                            {approvedOrgs.length > 1 && (
                                <div className="flex items-center gap-1.5 bg-slate-800/80 p-1 rounded-xl border border-indigo-500/30 overflow-x-auto max-w-full no-scrollbar">
                                    {approvedOrgs.map((org) => {
                                        const isActive = org.slug === currentOrg.organization_slug;
                                        return (
                                            <button
                                                key={org.id}
                                                type="button"
                                                onClick={() => setSelectedOrgSlug(org.slug)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer shrink-0 truncate max-w-[150px] ${
                                                    isActive
                                                        ? "bg-indigo-600 text-white shadow-xs"
                                                        : "text-slate-300 hover:text-white"
                                                }`}
                                                title={org.name}
                                            >
                                                {org.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Organization Admin Dashboard Action Button */}
                            {isCurrentOrgAdmin && (
                                <Link
                                    to={`/org/${currentOrg.organization_slug}/dashboard`}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-indigo-600/30 transition active:scale-95 flex-shrink-0"
                                >
                                    <LayoutDashboard className="w-4 h-4" />
                                    <span>Go to Organization Dashboard</span>
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            )}
                        </div>
                    </div>

                    {/* Assigned Courses & Deadlines within this workspace (if any) */}
                    {currentOrg.assigned_courses && currentOrg.assigned_courses.length > 0 && (
                        <div className="pt-3 border-t border-indigo-500/20 space-y-2.5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-200 flex items-center gap-1.5">
                                <BookOpen className="w-3.5 h-3.5 text-indigo-400" />
                                Assigned Courses & Deadlines ({currentOrg.assigned_courses.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {currentOrg.assigned_courses.map((ac: any) => (
                                    <Link
                                        key={`${currentOrg.organization_id}-${ac.course_id}`}
                                        to={`/courses/${ac.course_uuid || ac.course_id}`}
                                        className="rounded-xl border border-indigo-500/20 bg-slate-800/80 hover:bg-slate-800 hover:border-indigo-400/50 p-3.5 transition flex flex-col justify-between gap-2.5 group"
                                    >
                                        <div>
                                            <div className="flex items-start justify-between gap-2">
                                                <h4 className="text-xs sm:text-sm font-bold text-white group-hover:text-indigo-300 transition line-clamp-1">
                                                    {ac.course_title}
                                                </h4>
                                                <span
                                                    className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                        ac.is_mandatory
                                                            ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                                                            : "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"
                                                    }`}
                                                >
                                                    {ac.is_mandatory ? "Mandatory" : "Assigned"}
                                                </span>
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                                                <span className="inline-flex items-center gap-1">
                                                    <ShieldCheck className="w-3 h-3 text-indigo-400" />
                                                    Pass: {ac.passing_threshold_score ?? 70}%
                                                </span>
                                                {ac.due_date && (
                                                    <span className="inline-flex items-center gap-1 text-amber-300 font-medium">
                                                        <Calendar className="w-3 h-3" />
                                                        Due {new Date(ac.due_date).toLocaleDateString()}
                                                    </span>
                                                )}
                                                {!ac.due_date && ac.due_days_after_enrollment && (
                                                    <span className="inline-flex items-center gap-1 text-slate-400">
                                                        <Clock className="w-3 h-3" />
                                                        SLA: {ac.due_days_after_enrollment}d
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-300 mb-1">
                                                <span>{ac.completed_lessons}/{ac.total_lessons} lessons</span>
                                                <span className="text-indigo-400">{ac.progress_pct ?? 0}%</span>
                                            </div>
                                            <div className="w-full bg-slate-700/60 rounded-full h-1.5 overflow-hidden">
                                                <div
                                                    className="bg-indigo-500 h-1.5 rounded-full transition-all"
                                                    style={{ width: `${Math.min(100, ac.progress_pct || 0)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Skill Expectations & Workspace Learning Rules within this workspace (if any) */}
                    {((currentOrg.skill_expectations && currentOrg.skill_expectations.length > 0) ||
                        (currentOrg.learning_rules && currentOrg.learning_rules.length > 0)) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-indigo-500/20">
                            {currentOrg.skill_expectations && currentOrg.skill_expectations.length > 0 && (
                                <div className="rounded-xl border border-indigo-500/20 bg-slate-800/60 p-3 space-y-2">
                                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-indigo-300 flex items-center gap-1.5">
                                        <Target className="w-3.5 h-3.5 text-indigo-400" />
                                        Skill Expectations ({currentOrg.skill_expectations.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-1.5">
                                        {currentOrg.skill_expectations.map((sk: any) => (
                                            <span
                                                key={sk.id}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-950/60 border border-indigo-500/30 text-xs font-semibold text-indigo-200"
                                            >
                                                <span>{sk.skill_name}</span>
                                                <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-bold">
                                                    {sk.target_proficiency}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {currentOrg.learning_rules && currentOrg.learning_rules.length > 0 && (
                                <div className="rounded-xl border border-indigo-500/20 bg-slate-800/60 p-3 space-y-2">
                                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
                                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                                        Workspace Learning Rules ({currentOrg.learning_rules.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-1.5">
                                        {currentOrg.learning_rules.map((rule: any) => (
                                            <span
                                                key={rule.id}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/30 text-xs font-semibold text-emerald-200"
                                            >
                                                <span>{rule.course_title || `Course #${rule.course_id}`}</span>
                                                <span className="text-[10px] font-bold text-emerald-400">
                                                    • {rule.passing_threshold_score}% Pass
                                                    {rule.due_days_after_enrollment ? ` • ${rule.due_days_after_enrollment}d SLA` : ""}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!isPendingOnlyMember && (
                <>
                    <div className="rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1 sm:mb-2">My Courses</h1>
                            <p className="text-sm sm:text-base text-slate-600">Track your progress and continue learning.</p>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="rounded-2xl border border-slate-200 bg-white/95 p-3 sm:p-4 shadow-sm">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search courses..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                                />
                            </div>
                        </div>
                    </div>

                    <CoursesGrid
                        page={page}
                        limit={limit}
                        searchQuery={searchQuery}
                        cacheKey={`${cacheKey}:${currentOrg?.organization_slug || "default"}`}
                        organizationSlug={currentOrg?.organization_slug}
                        isOrgAdmin={isCurrentOrgAdmin}
                        enabled={!isProfileLoading}
                    />
                </>
            )}
        </div>
    );
}

function CoursesGrid({
    page,
    limit,
    searchQuery,
    cacheKey,
    organizationSlug,
    isOrgAdmin,
    enabled = true,
}: {
    page: number;
    limit: number;
    searchQuery: string;
    cacheKey: string;
    organizationSlug?: string | null;
    isOrgAdmin?: boolean;
    enabled?: boolean;
}) {
    const nav = useCourseNavigation();
    const hasMounted = useSyncExternalStore(
        () => () => {},
        () => true,
        () => false,
    );
    const cachedFromNav = hasMounted ? (nav?.getCoursesListCache(cacheKey) ?? null) : null;
    const lastWrittenRef = useRef<CourseCardData[] | null>(null);

    const sameCourseList = (a: CourseCardData[] | null | undefined, b: CourseCardData[] | null | undefined) => {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) return false;
            if (a[i].progress !== b[i].progress) return false;
        }
        return true;
    };

    // Debounce the free-text search so typing doesn't fire a request per
    // keystroke.
    const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(searchQuery), 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const qParam = debouncedQuery.trim() || undefined;

    const coursesQuery = useQuery({
        queryKey: ["student-courses-list", page, limit, qParam, organizationSlug],
        queryFn: async () => {
            const queryParams: Record<string, any> = { page, limit, q: qParam };
            if (organizationSlug) {
                queryParams.organization_slug = organizationSlug;
            }
            const { data, error, response } = await api.listPublishedCoursesApiV1CoursesGet({
                query: queryParams as any
            });
            if (error) throw new Error(`Failed to fetch courses list (${response?.status})`);
            return Array.isArray(data) ? (data as unknown as CourseCardData[]) : ((data as any).courses || []);
        },
        enabled,
        initialData: hasMounted && !qParam ? (cachedFromNav ?? undefined) : undefined,
        staleTime: 30_000,
    });

    const courses = coursesQuery.data ?? (!qParam ? cachedFromNav : null) ?? [];

    useEffect(() => {
        // Only cache the unfiltered base page — a searched/track-filtered
        // result is not the full page list and would poison the preview
        // cache used when navigating back from a course.
        if (qParam) return;
        if (!coursesQuery.data) return;
        const setCache = nav?.setCoursesListCache;
        if (!setCache) return;
        if (sameCourseList(lastWrittenRef.current, coursesQuery.data)) return;
        if (sameCourseList(cachedFromNav, coursesQuery.data)) {
            lastWrittenRef.current = coursesQuery.data;
            return;
        }
        lastWrittenRef.current = coursesQuery.data;
        setCache(cacheKey, coursesQuery.data);
    }, [cacheKey, cachedFromNav, coursesQuery.data, nav?.setCoursesListCache, qParam]);

    const handleCourseClick = (e: React.MouseEvent, course: CourseCardData) => {
        const courseParam = course.uuid || course.id;
        nav?.setCourseListPreview(courseParam, course);
    };

    // Search filtering happens server-side (see F11 in
    // docs/api_performance_audit.md) so it applies across all pages, not
    // just the courses already fetched for this one.
    const filteredCourses = courses ?? [];

    if (coursesQuery.isLoading && courses.length === 0) {
        return <CoursesGridSkeleton />;
    }

    if (filteredCourses.length === 0) {
        if (qParam) {
            return (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 p-6 space-y-2 shadow-xs">
                    <p className="text-sm font-semibold text-slate-800">No courses found matching &ldquo;{qParam}&rdquo;.</p>
                    <p className="text-xs text-slate-500">Try searching with different keywords.</p>
                </div>
            );
        }

        if (organizationSlug) {
            return (
                <div className="text-center py-12 sm:py-16 bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8 space-y-3.5 shadow-xs">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mx-auto shadow-2xs">
                        <BookOpen className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-base sm:text-lg font-bold text-slate-900">
                            No courses assigned yet
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
                            {isOrgAdmin
                                ? "No courses have been assigned to your organization yet. You can assign courses directly from your Organization Dashboard."
                                : "No courses have been assigned to your workspace yet. Please check back later or contact your organization administrator."}
                        </p>
                    </div>
                    {isOrgAdmin && (
                        <div className="pt-2">
                            <Link
                                to={`/org/${organizationSlug}/dashboard`}
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-xl shadow-xs transition"
                            >
                                <LayoutDashboard className="w-4 h-4" />
                                <span>Assign Courses in Organization Dashboard</span>
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <p className="text-slate-500">No courses available at this time.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCourses.map((course: CourseCardData) => (
                <CourseCard
                    key={course.id}
                    course={course}
                    onCourseClick={handleCourseClick}
                />
            ))}
        </div>
    );
}
